import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function Photos({ projectId, year, month }) {
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!projectId) return;
    try {
      const r = await api.get("/attendance/photos/grid", {
        params: { project_id: projectId, year, month },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId, year, month]);

  const dates = useMemo(() => {
    const n = data?.days_in_month || 0;
    return Array.from({ length: n }, (_, i) =>
      `${year}-${pad(month)}-${pad(i + 1)}`
    );
  }, [data, year, month]);

  return (
    <div className="space-y-5" data-testid="photos-page">
      {!data || data.workers.length === 0 ? (
        <Card className="p-10 text-center text-[#475569]">
          No employees on this project.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-3 bg-slate-50/40 border-b">
            <Input
              data-testid="photos-search"
              placeholder="Search employees…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-xs"
            />
            <div className="text-xs text-[#475569]">
              {(() => {
                const ql = q.trim().toLowerCase();
                const c = ql
                  ? data.workers.filter((w) =>
                      `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                    ).length
                  : data.workers.length;
                return `${c} / ${data.workers.length}`;
              })()}
            </div>
          </div>
          <table className="w-full border-collapse table-fixed">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold text-[#475569] sticky left-0 bg-slate-50 z-10 w-[140px]">
                  Employee
                </th>
                {dates.map((d) => (
                  <th
                    key={d}
                    className="px-0.5 py-2 text-[10px] font-semibold text-[#475569]"
                  >
                    {parseInt(d.split("-")[2])}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ql = q.trim().toLowerCase();
                const filtered = ql
                  ? data.workers.filter((w) =>
                      `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                    )
                  : data.workers;
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={dates.length + 1} className="px-3 py-10 text-center text-[#475569]">
                        No employees match "{q}".
                      </td>
                    </tr>
                  );
                }
                return filtered.map((w) => (
                <tr key={w.id} className="border-t" data-testid={`photo-row-${w.worker_id}`}>
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 border-r w-[140px]">
                    <div className="text-xs font-semibold text-[#1A3A5C] truncate max-w-[130px]">
                      {w.name}
                    </div>
                    <div className="text-[10px] font-mono text-[#475569]">
                      {w.worker_id}
                    </div>
                  </td>
                  {dates.map((d) => {
                    const p = data.photos[w.id]?.[d];
                    const hasIn = !!p?.selfie_base64;
                    const hasOut = !!p?.check_out_selfie_base64;
                    return (
                      <td key={d} className="p-0.5 text-center">
                        <div className="flex flex-col gap-0.5 items-center justify-center">
                          {/* Check-in photo button */}
                          <button
                            onClick={() => hasIn && setViewing({ ...p, name: w.name, worker_id: w.worker_id, date: d })}
                            disabled={!hasIn}
                            className={`mx-auto aspect-square w-full max-w-[20px] rounded overflow-hidden ${hasIn ? "ring-1 ring-green-300 hover:ring-2 hover:ring-green-500 cursor-pointer" : "bg-slate-50 border border-slate-200"} block`}
                            title={`${d} - Check-in`}
                          >
                            {hasIn && (
                              <img src={p.selfie_base64} alt="In" className="w-full h-full object-cover" />
                            )}
                          </button>
                          {/* Check-out photo button */}
                          <button
                            onClick={() => hasOut && setViewing({ ...p, name: w.name, worker_id: w.worker_id, date: d })}
                            disabled={!hasOut}
                            className={`mx-auto aspect-square w-full max-w-[20px] rounded overflow-hidden ${hasOut ? "ring-1 ring-blue-300 hover:ring-2 hover:ring-blue-500 cursor-pointer" : "bg-slate-50 border border-slate-200"} block`}
                            title={`${d} - Check-out`}
                          >
                            {hasOut && (
                              <img src={p.check_out_selfie_base64} alt="Out" className="w-full h-full object-cover" />
                            )}
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {viewing?.name} ({viewing?.worker_id}) — {viewing?.date}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-[#475569] mb-1 flex items-center gap-2">
                    Check-in
                    {viewing.within_radius != null && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          viewing.within_radius
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {viewing.within_radius ? "WITHIN RANGE" : "OUT OF RANGE"}
                      </span>
                    )}
                  </div>
                  {viewing.selfie_base64 ? (
                    <img
                      src={viewing.selfie_base64}
                      alt="Check-in selfie"
                      className="w-full rounded-lg border"
                    />
                  ) : (
                    <div className="bg-slate-50 rounded-lg border p-6 text-center text-sm text-[#475569]">
                      No selfie
                    </div>
                  )}
                  {viewing.check_in_at && (
                    <div className="text-xs text-[#475569] mt-1">
                      Time: <strong>{fmtTime(viewing.check_in_at)}</strong>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#475569] mb-1">
                    Check-out
                  </div>
                  {viewing.check_out_selfie_base64 ? (
                    <img
                      src={viewing.check_out_selfie_base64}
                      alt="Check-out selfie"
                      className="w-full rounded-lg border"
                    />
                  ) : (
                    <div className="bg-slate-50 rounded-lg border p-6 text-center text-sm text-[#475569]">
                      Not checked out yet
                    </div>
                  )}
                  {viewing.check_out_at && (
                    <div className="text-xs text-[#475569] mt-1">
                      Time: <strong>{fmtTime(viewing.check_out_at)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
